import { FormControl, FormHelperText, InputLabel, MenuItem, Select as MUISelect, SelectChangeEvent } from '@mui/material';
import classNames from 'classnames';
import { memo, useCallback, useMemo } from 'react';
import { useLocalization } from '../../../../../Contexts/Localization';
import { GMIconButton, GMInfoIcon } from '../../../../Buttons/IconButton';
import { GMOutlinedInputLabel } from '../../../InputLabel';
import { GMInputWrapper } from '../../../InputWrapper';
import css from './Select.module.scss';
import { ErrorLevel } from '../../../../../API/Enums';
import { GMSelectOption, SelectProps } from './Select.types';

/**
 * Generates an array of GMSelectOption where the label and value are the same string value
 *
 * @param data string or number array
 * @returns list select item array where the value and label are equal
 */
export const SelectItemsFromArray = (data: string[] | number[]): GMSelectOption[] => {
    return data.map((d) => {
        return {
            value: d.toString(),
            label: d.toString(),
        };
    });
};

const Select: React.FC<SelectProps> = memo(({
    name,
    value,
    options,
    onChange,
    onBlur,
    onFocus,
    endAdornment,
    emptyOption,
    emptyOptionDisplay,
    label,
    shrinkLabel,
    helperText,
    ...wrapperProps
}) => {
    const Localization = useLocalization();

    const onSelectChange = useCallback((e: SelectChangeEvent<string>) => {
        onChange(e.target.value);
    }, [onChange]);

    const isValueValid = useMemo<boolean>(() => {
        return !!value && options.findIndex((o) => o.value === value) > -1;
    }, [value, options]);

    const renderValue = useCallback((value: string) => {
        const selectedOption: GMSelectOption | undefined = options.find((o) => o.value === value);

        if (selectedOption) {
            return selectedOption.label ?? emptyOptionDisplay;
        }

        return emptyOptionDisplay ?? '';
    }, [emptyOptionDisplay, options]);

    const errorState: boolean = wrapperProps.error !== undefined && wrapperProps.error !== ErrorLevel.None;

    return (
        <GMInputWrapper {...wrapperProps}>
            <FormControl variant={wrapperProps.variant} disabled={wrapperProps.disabled}>
                {label ?
                    wrapperProps.variant === 'outlined' ?
                        <GMOutlinedInputLabel
                            disabled={wrapperProps.disabled}
                            error={wrapperProps.error}
                            required={wrapperProps.required}
                            label={label}
                        /> :
                        <InputLabel
                            disabled={wrapperProps.disabled}
                            variant={wrapperProps.variant}
                            id={`select-label-${label}`}
                            shrink={wrapperProps.hideShrinkLabel && !value ? false : shrinkLabel ? true : undefined}
                            error={errorState}
                            required={wrapperProps.required}
                        >
                            {label}
                        </InputLabel>
                    : null
                }
                <MUISelect
                    variant={wrapperProps.variant}
                    name={name}
                    label={wrapperProps.variant === 'standard' ? label : undefined}
                    labelId={label && wrapperProps.variant === 'standard' ? `select-label-${label}` : undefined}
                    value={isValueValid ? value : ''}
                    onChange={onSelectChange}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    disabled={wrapperProps.disabled}
                    className={classNames(
                        endAdornment ? css.hasEndAdornment : '',
                        shrinkLabel ? css.forceShrinkLabel : '',
                    )}
                    MenuProps={{
                        className: css.menu,
                    }}
                    renderValue={renderValue}
                    displayEmpty={!!emptyOptionDisplay}
                    error={errorState}
                    required={wrapperProps.required}
                    endAdornment={endAdornment ?
                        (endAdornment.onClick ?
                            <GMIconButton
                                color={endAdornment.color ?? wrapperProps.color}
                                tooltip={endAdornment.tooltip}
                                notTabbable
                                disabled={wrapperProps.disabled}
                                onClick={endAdornment.onClick}
                            >
                                {endAdornment.icon}
                            </GMIconButton>
                            : <GMInfoIcon
                                tooltip={endAdornment.tooltip}
                                color={endAdornment.color ?? wrapperProps.color}
                            />
                        )
                        : null
                    }

                >
                    {emptyOption ?
                        <MenuItem value={''}>
                            <em>{Localization.None}</em>
                        </MenuItem> : null
                    }
                    {options.map((option, index) => {
                        return (
                            <MenuItem
                                key={`select-${label}-option-${index}`}
                                value={option.value}
                                className={css.menuItem}
                                disabled={option.disabled}
                            >
                                {option.menuOptionLabel ? option.menuOptionLabel : option.label}
                            </MenuItem>
                        );
                    })}
                </MUISelect>
                {helperText ?
                    <FormHelperText
                        error={errorState}
                        disabled={wrapperProps.disabled}
                    >
                        {helperText}
                    </FormHelperText>
                    : null
                }
            </FormControl>
        </GMInputWrapper >
    );
});

export default Select;